import {
  Alert,
  Button,
  DietaryBadge,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  formatDate,
  formatDateTime,
  useToast,
} from '@catering/ui';
import { Link, useParams } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useState } from 'react';

import { ApiError } from '../api/client';
import { useCurrentUser } from '../auth/use-current-user';
import type { Order, OrderChangeRequest } from './api';
import { ChangeRequestFormModal } from './change-request-form-modal';
import { describeOrderError } from './error-messages';
import { hasOrderPermission } from './order-permissions';
import { useCancelOrder, useCreateChangeRequest, useOrder, useSubmitOrder } from './use-order';

type DetailTab = 'info' | 'items' | 'cambios';

const CHANGE_REQUEST_STATUS_LABELS: Record<string, string> = {
  PENDING: 'En revisión',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  APPLIED: 'Aplicada',
};

/** One line per requested position, as "Plato principal: 10 → 15" against what the order has now. */
function describeRequestedLines(request: OrderChangeRequest, order: Order): string[] {
  return (request.requestedChanges?.items ?? []).flatMap((line) => {
    const current = order.items.find((item) => item.positionId === line.positionId);

    if (current === undefined) {
      return [];
    }

    const label = current.position?.name ?? current.menuNameSnapshot;

    return [`${label}: ${current.quantity} → ${line.quantity}`];
  });
}

function isDirectlyEditable(status: string): boolean {
  return status === 'DRAFT' || status === 'SUBMITTED';
}

/**
 * `portal-orders/spec.md` → "Detalle de pedido sin monto total",
 * "Bloqueo de edición directa ofrece 'Solicitar cambio'", "Estado de la
 * solicitud visible en el detalle del pedido". `Order`/`OrderItem`
 * (`orders/api.ts`) never carry a price field, so there is nothing to
 * accidentally render here. `remitos` links (RF-06-13) are not wired:
 * no `apps/portal` endpoint exposes delivery notes yet, out of this PR's
 * restricted edit surface.
 */
export function OrderDetailPage(): JSX.Element {
  const params = useParams({ strict: false });
  const orderId = params.orderId;
  const [tab, setTab] = useState<DetailTab>('info');
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const { toast } = useToast();
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const order = useOrder(orderId);
  const currentUser = useCurrentUser();
  const createChangeRequest = useCreateChangeRequest(orderId ?? '');
  const cancelOrder = useCancelOrder(orderId ?? '');
  const submitOrder = useSubmitOrder();

  if (order.isPending) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  if (order.isError) {
    if (order.error instanceof ApiError && order.error.status === 404) {
      return (
        <Alert tone="danger" title="Pedido no encontrado">
          <Link to="/orders/history" className="underline">
            Volver al historial
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar el pedido">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void order.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const data = order.data;
  const roleCodes = currentUser.data?.roleCodes ?? [];
  const canUpdate = hasOrderPermission(roleCodes, 'order.update');
  const canCancel = hasOrderPermission(roleCodes, 'order.cancel');
  const editable = isDirectlyEditable(data.status);
  const canRequestChange = data.status === 'CONFIRMED' && canUpdate;
  const hasPendingChangeRequest = data.changeRequests.some((request) => request.status === 'PENDING');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{data.orderNumber}</h1>
          <p className="text-sm text-slate-500">
            {data.siteName} · {formatDate(data.serviceDate)}
          </p>
        </div>
        <StatusBadge domain="order" status={data.status} />
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as DetailTab)}>
        <TabList aria-label="Pestañas del pedido">
          <Tab value="info">Info</Tab>
          <Tab value="items">Platos</Tab>
          <Tab value="cambios">Cambios</Tab>
        </TabList>

        <TabPanel value="info">
          <div className="flex flex-col gap-3">
            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-slate-500">Sede</dt>
                <dd className="font-medium text-slate-900">{data.siteName}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Fecha de servicio</dt>
                <dd className="font-medium text-slate-900">{formatDate(data.serviceDate)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Cantidad total</dt>
                <dd className="font-medium text-slate-900">{data.totalQuantity}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Observaciones</dt>
                <dd className="font-medium text-slate-900">{data.clientNotes ?? '—'}</dd>
              </div>
            </dl>

            {data.status === 'DRAFT' && canUpdate ? (
              <Alert tone="warning">
                <div className="flex flex-col gap-2">
                  <span>Este pedido todavía no fue enviado.</span>
                  {submitOrder.isError ? <span>{describeOrderError(submitOrder.error)}</span> : null}
                  <div>
                    <Button
                      size="sm"
                      loading={submitOrder.isPending}
                      onClick={() =>
                        void submitOrder
                          .mutateAsync(data.id)
                          .then(() => toast('success', 'Pedido enviado.'))
                          .catch(() => undefined)
                      }
                    >
                      Enviar pedido
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : null}

            {editable && (canUpdate || canCancel) ? (
              <div className="flex items-center gap-3">
                {canUpdate ? (
                  <Link
                    to="/orders/$orderId/edit"
                    params={{ orderId: data.id }}
                    className="inline-flex"
                  >
                    <Button>Editar</Button>
                  </Link>
                ) : null}
                {canCancel ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      cancelOrder.reset();
                      setCancelling(true);
                    }}
                  >
                    Cancelar
                  </Button>
                ) : null}
              </div>
            ) : null}

            {canRequestChange ? (
              <div>
                <Button
                  disabled={hasPendingChangeRequest}
                  onClick={() => setChangeRequestOpen(true)}
                >
                  Solicitar cambio
                </Button>
                {hasPendingChangeRequest ? (
                  <p className="mt-2 text-sm text-slate-600">
                    Ya tenés una solicitud de cambio en revisión para este pedido. Te avisamos
                    cuando operaciones la resuelva; mientras tanto no podés enviar otra.
                  </p>
                ) : null}
              </div>
            ) : null}

            {cancelling ? (
              <div className="flex flex-col gap-2 rounded-md border border-slate-200 p-3">
                {cancelOrder.isError ? (
                  <Alert tone="danger">{describeOrderError(cancelOrder.error)}</Alert>
                ) : null}
                <label htmlFor="cancel-reason" className="text-sm font-medium text-slate-900">
                  Motivo de la cancelación
                </label>
                <input
                  id="cancel-reason"
                  placeholder="Contanos por qué cancelás"
                  value={cancelReason}
                  onChange={(event) => setCancelReason(event.target.value)}
                  className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                />
                <div className="flex items-center gap-3">
                  <Button
                    loading={cancelOrder.isPending}
                    disabled={cancelReason.trim() === ''}
                    onClick={() =>
                      void cancelOrder
                        .mutateAsync(cancelReason)
                        .then(() => {
                          setCancelling(false);
                          setCancelReason('');
                          toast('success', 'Pedido cancelado.');
                        })
                        .catch(() => undefined)
                    }
                  >
                    Confirmar cancelación
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCancelling(false);
                      setCancelReason('');
                    }}
                  >
                    Volver
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </TabPanel>

        <TabPanel value="items">
          <div className="flex flex-col gap-3">
            {data.items.map((item) => (
              <div key={item.id} className="rounded-md border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-col">
                    {item.position !== null ? (
                      <span className="text-xs uppercase tracking-wide text-slate-500">
                        {item.position.name}
                      </span>
                    ) : null}
                    <span className="text-sm font-medium text-slate-900">
                      {item.menuNameSnapshot}
                    </span>
                  </div>
                  <span className="text-sm text-slate-700">Cantidad: {item.quantity}</span>
                </div>
                {item.dietaryTags.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {item.dietaryTags.map((tag) => (
                      <DietaryBadge key={tag} code={tag} />
                    ))}
                  </div>
                ) : null}
                {item.allergens.length > 0 ? (
                  <p className="text-xs text-slate-500">Alérgenos: {item.allergens.join(', ')}</p>
                ) : null}
                {item.notes !== null && item.notes !== '' ? (
                  <p className="text-xs text-slate-500">{item.notes}</p>
                ) : null}
              </div>
            ))}
          </div>
        </TabPanel>

        <TabPanel value="cambios">
          <div className="flex flex-col gap-3">
            {data.changeRequests.length === 0 ? (
              <p className="text-sm text-slate-500">Este pedido no tiene solicitudes de cambio.</p>
            ) : (
              data.changeRequests.map((request) => (
                <div key={request.id} className="rounded-md border border-slate-200 bg-white p-3">
                  <p className="text-sm font-medium text-slate-900">
                    {CHANGE_REQUEST_STATUS_LABELS[request.status] ?? request.status}
                  </p>
                  <p className="text-xs text-slate-500">
                    Solicitada el {formatDateTime(request.createdAt)}
                  </p>
                  {describeRequestedLines(request, data).map((line) => (
                    <p key={line} className="text-sm text-slate-700">
                      {line}
                    </p>
                  ))}
                  {request.rejectionReason !== null ? (
                    <p className="text-xs text-slate-500">
                      Motivo del rechazo: {request.rejectionReason}
                    </p>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </TabPanel>
      </Tabs>

      {changeRequestOpen ? (
        <ChangeRequestFormModal
          order={data}
          submitting={createChangeRequest.isPending}
          error={createChangeRequest.error}
          onClose={() => {
            createChangeRequest.reset();
            setChangeRequestOpen(false);
          }}
          onSubmit={async (items) => {
            try {
              await createChangeRequest.mutateAsync(items);
              setChangeRequestOpen(false);
              toast('success', 'Solicitud de cambio enviada. Operaciones la va a revisar.');
            } catch {
              // `error` is already surfaced via `createChangeRequest.error` in the modal.
            }
          }}
        />
      ) : null}
    </div>
  );
}
